import { Component, OnDestroy, OnInit } from '@angular/core';
import { LoaderOptions, WorkflowLoaderEvent } from '@shared/model/workflow-loader.model';
import { WorkflowLoaderService } from '@shared/service/workflow-loader.service';
import { delay, interval, of, Subscription, takeWhile, tap } from 'rxjs';

const SEGMENT_UPDATE_LEN = 600;

@Component({
  selector: 'dx-workflow-loader',
  templateUrl: './workflow-loader.component.html',
  styleUrl: './workflow-loader.component.scss'
})
export class WorkflowLoaderComponent implements OnInit, OnDestroy {
  #totalSegments?: number;
  #listener?: Subscription;

  public isVisible = false;
  public message: string;
  public options: LoaderOptions;
  public currentSegment = 0;
  public triggerBubbleAnimation = false;
  public triggerTextAnimation = false;
  public progress = 0;
  public isComplete = false;
  public backgroundMode = 'bl-bg-color--snow';

  constructor(public workflowLoaderService: WorkflowLoaderService) {}

  public ngOnInit(): void {
    this.handleState(this.workflowLoaderService.state || { type: 'init' });
    this.#listener = this.workflowLoaderService.loaderState$.subscribe((event) => this.handleState(event));
  }

  public get totalSegments(): number {
    return this.#totalSegments;
  }

  /**
   * Updates the current progress of the loader on ui based on current segment
   */
  private updateProgress(): void {
    this.progress = (this.currentSegment / (this.#totalSegments - 1)) * 100;
  }

  /**
   * Switch statement to handle the different states of the loader
   * and keep segments and options in sync with the loader service.
   *
   */
  private handleState(event: WorkflowLoaderEvent): void {
    const initialState = this.workflowLoaderService.initial;
    this.message = this.workflowLoaderService.state.message;
    this.options = initialState?.options || {}; // no object assign as we want to start new
    this.currentSegment = this.workflowLoaderService.segmentInfo?.activeSegment || 0;
    this.#totalSegments = this.workflowLoaderService.segmentInfo?.totalSegments || 0;
    switch (event.type) {
      case 'start':
        this.onStart(event);
        break;
      case 'next':
        this.onNext(event);
        break;
      case 'end':
        this.onEnd(event);
        break;
      case 'show':
        this.onShow();
        break;
      case 'hide':
        this.onHide();
        break;
      case 'clear':
      case 'init':
        this.onClear();
        break;
    }
  }

  /** sets the background mode while asserting we have at least default set */
  private setBackground(opts: LoaderOptions): void {
    if (opts.overlayMode === 'darken') {
      this.backgroundMode = 'loader-bg--darken'; // need the darken class
    } else {
      this.backgroundMode = 'bl-bg-color--snow';
      opts.overlayMode = opts.overlayMode || 'full'; // ensure we are set
    }
  }

  /** Esnures we have an animation id, even if default. To show nothing pass 'none' */
  private setAnimation(opts: LoaderOptions): void {
    opts.animationId = opts?.animationId || 'default';
  }

  /**
   * Triggers a clean start - reseting all data and options
   * @param event The start event
   */
  private onStart(event: WorkflowLoaderEvent): void {
    this.message = event.message;
    this.options = event.options || {}; // no object assign as we want to start new
    this.setBackground(this.options);
    this.setAnimation(this.options);
    this.triggerBubbleAnimation = true;
    if (this.workflowLoaderService.segmentInfo.hasSegments) {
      this.currentSegment = this.workflowLoaderService.segmentInfo.activeSegment;
      this.#totalSegments = this.workflowLoaderService.segmentInfo.totalSegments;
    } else {
      this.currentSegment = 0;
      this.#totalSegments = 0;
    }
    this.isVisible = true;
  }

  /**
   * Moves the segment option to the next, with any updates such as message.
   * @param event The next event
   */
  private onNext(event: WorkflowLoaderEvent): void {
    this.triggerBubbleAnimation = false;
    this.triggerTextAnimation = false;
    this.isVisible = true;

    this.options = Object.assign(this.options || {}, event.options || {});
    this.setAnimation(this.options);
    this.setBackground(this.options);
    this.currentSegment = this.workflowLoaderService.segmentInfo.activeSegment;
    this.#totalSegments = this.workflowLoaderService.segmentInfo.totalSegments;
    this.updateProgress();

    of('')
      .pipe(
        delay(SEGMENT_UPDATE_LEN / 6),
        tap(() => (this.triggerBubbleAnimation = true)),
        tap(() => {
          // only animate when the message changes
          if (this.message !== event.message) {
            this.triggerTextAnimation = true;
          }
        }),
        delay(SEGMENT_UPDATE_LEN / 6),
        tap(() => (this.message = event.message))
      )
      .subscribe();
  }

  /**
   * Moves the segment animation to the end and completes any animations before hiding.
   * @param event The end event data.
   */
  private onEnd(event: WorkflowLoaderEvent): void {
    this.triggerBubbleAnimation = false;
    this.triggerTextAnimation = false;
    this.options = Object.assign(this.options || {}, event.options || {});
    this.setAnimation(this.options);
    this.setBackground(this.options);

    /**
     * Needed to move progress bar along immediately
     */
    this.currentSegment++;
    this.#totalSegments = this.workflowLoaderService.segmentInfo.totalSegments;
    this.updateProgress();

    /**
     * Trigger text animation for end from any point
     */
    // on end we want to leave the last message, not clear
    // if they pass an empty string we can clear
    if (typeof event.message === 'string') {
      of('')
        .pipe(
          delay(SEGMENT_UPDATE_LEN / 6),
          tap(() => {
            // only animate when the message changes
            if (this.message !== event.message) {
              this.triggerTextAnimation = true;
            }
          }),
          delay(SEGMENT_UPDATE_LEN / 4),
          tap(() => {
            this.message = event.message;
          })
        )
        .subscribe();
    }

    interval(SEGMENT_UPDATE_LEN / 6)
      .pipe(
        takeWhile(() => this.currentSegment < this.#totalSegments - 1),
        tap(() => {
          this.currentSegment++;
          this.updateProgress();
        })
      )
      .subscribe({
        complete: () => {
          /**
           * Delay to prevent last bubble from filling at the same time as bubble before it
           */
          of('')
            .pipe(
              delay(SEGMENT_UPDATE_LEN / 12),
              tap(() => (this.isComplete = true)),
              delay(SEGMENT_UPDATE_LEN * 2),
              tap(() => this.onClear())
            )
            .subscribe(() => {
              this.isVisible = false;
            });
        }
      });
  }

  /**
   * Shows the loader in the current state,
   * recommend knowing your state if you are
   * going to call this.
   */
  private onShow(): void {
    this.isVisible = true;
  }

  /**
   * Hides the loader in the current state,
   * recommend noting your state here if planning to call
   * show again later.
   */
  private onHide(): void {
    this.isVisible = false;
  }

  /**
   * Clears and resets the animation and loader, while hiding it.
   */
  private onClear(): void {
    this.isVisible = false;
    setTimeout(() => {
      this.options = {};
      this.message = '';
      this.currentSegment = this.workflowLoaderService.segmentInfo.activeSegment;
      this.#totalSegments = this.workflowLoaderService.segmentInfo.totalSegments;
      this.progress = 0;
      this.triggerBubbleAnimation = false;
      this.isComplete = false;
      this.isVisible = false;
    });
  }

  public ngOnDestroy(): void {
    this.#listener?.unsubscribe();
  }
}
